import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import wsfeTipCompService, { WsfeTipCompDto } from '../../services/wsfeTipCompService';

const emptyForm: WsfeTipCompDto = {
  nroTipoFormulario: '',
  letra: '',
  codAfip: '',
  codAfip2: '',
};

const WsfeTipComp: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<WsfeTipCompDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<WsfeTipCompDto>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async (search?: string) => {
    try {
      const data = await wsfeTipCompService.getAll(search);
      setItems(data);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar WSFE_TipComp', { variant: 'error' });
      setItems([]);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void cargar(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, cargar]);

  const abrirNuevo = () => {
    setForm({ ...emptyForm });
    setEditando(false);
    setDialogOpen(true);
  };

  const abrirEditar = (item: WsfeTipCompDto) => {
    setForm({ ...item });
    setEditando(true);
    setDialogOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'letra') {
      setForm((prev) => ({ ...prev, letra: value.slice(0, 1).toUpperCase() }));
      return;
    }
    if (name === 'nroTipoFormulario') {
      setForm((prev) => ({ ...prev, nroTipoFormulario: value.slice(0, 2) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nroTipoFormulario.trim() || !form.letra.trim()) {
      enqueueSnackbar('Completá tipo de formulario y letra', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editando) {
        await wsfeTipCompService.update(form.nroTipoFormulario, form.letra, form);
      } else {
        await wsfeTipCompService.create(form);
      }
      enqueueSnackbar('Mapeo guardado correctamente', { variant: 'success' });
      setDialogOpen(false);
      await cargar(searchTerm);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (item: WsfeTipCompDto) => {
    if (!window.confirm(`¿Eliminar el mapeo ${item.nroTipoFormulario} / ${item.letra}?`)) return;
    try {
      await wsfeTipCompService.delete(item.nroTipoFormulario, item.letra);
      enqueueSnackbar('Mapeo eliminado', { variant: 'success' });
      await cargar(searchTerm);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Tipos de comprobante ARCA
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Mapeo de tipo de formulario y letra al código AFIP (WSFE_TipComp).
              </Typography>
            </Box>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={abrirNuevo}>
              Nuevo mapeo
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por tipo, letra o código AFIP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {items.map((item) => (
                <Card key={`${item.nroTipoFormulario}-${item.letra}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Tipo {item.nroTipoFormulario} · Letra {item.letra}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AFIP: {item.codAfip || '-'} {item.codAfip2 ? `· AFIP 2: ${item.codAfip2}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" color="primary" onClick={() => abrirEditar(item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleEliminar(item)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Letra</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código AFIP</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código AFIP 2</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={`${item.nroTipoFormulario}-${item.letra}`} hover>
                      <TableCell>{item.nroTipoFormulario}</TableCell>
                      <TableCell>{item.letra}</TableCell>
                      <TableCell>{item.codAfip || '-'}</TableCell>
                      <TableCell>{item.codAfip2 || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => abrirEditar(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleEliminar(item)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleGuardar}>
          <DialogTitle>{editando ? 'Editar mapeo ARCA' : 'Nuevo mapeo ARCA'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                name="nroTipoFormulario"
                label="Tipo de formulario"
                value={form.nroTipoFormulario}
                onChange={handleChange}
                required
                disabled={editando}
                helperText="Código interno (ej. 1 = factura, 2 = nota de crédito)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                name="letra"
                label="Letra"
                value={form.letra}
                onChange={handleChange}
                required
                disabled={editando}
                helperText="A, B, C, E, M, etc."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                name="codAfip"
                label="Código AFIP"
                value={form.codAfip}
                onChange={handleChange}
                helperText="Ej. 001 Factura A, 006 Factura B, 011 Factura C"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                name="codAfip2"
                label="Código AFIP 2"
                value={form.codAfip2}
                onChange={handleChange}
                helperText="Opcional"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>
              Guardar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default WsfeTipComp;
